import { useMemo, useEffect, useCallback, type MouseEvent } from 'react';
import { Helmet } from 'react-helmet-async';
import {
	Card,
	CardContent,
	CardActions,
	Stack,
	Grid,
	List,
	ListItem,
	ListItemText,
	Typography,
	Button,
	Box,
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';

import { useFormik } from 'formik';

import { BulkRequest } from '../../../models/bulkRequest.types';
import { fCurrency } from '../../../utils/formatNumber';
import { Payment } from '../../../utils/payment';
import Toast from '../../../components/toast';
import { TestSelectionSkeleton } from '../../../components/shared';
import {
	useGetBulkRequestQuery,
	useGetBulkRequestPaymentSummaryQuery,
	useUpdateBulkRequestMutation,
	useCancelBulkRequestMutation,
} from '../../../slices/apis/bulkRequestApi';
import { useConfirm } from '../../../hooks/useConfirm';
import { useBulkRequestRules } from '../../../hooks/useRequestRules';
import { BulkRequestLocation, BulkRequestTest } from '../steps';

export default function BulkRequestEdit() {
	const navigate = useNavigate();
	const { reqId } = useParams<{ reqId: string }>();
	const { allowQtyEditing } = useBulkRequestRules(reqId ?? '');
	const theme = useTheme();
	const { data: bulkRequestData } = useGetBulkRequestQuery(reqId as string);
	const { data: paymentSummary } = useGetBulkRequestPaymentSummaryQuery(reqId as string);
	const [
		updateBulkRequest,
		{ isError: isUpdateRequestError, isLoading: isUpdateRequestLoading, isSuccess: isUpdateRequestSuccess },
	] = useUpdateBulkRequestMutation();
	const [
		cancelBulkRequest,
		{ isLoading: isCancelRequestLoading, isSuccess: isCancelRequestSuccess, isError: isCancelRequestError },
	] = useCancelBulkRequestMutation();
	const { Confirm, confirmProps } = useConfirm();

	const initialValues = useMemo(() => {
		const defaults = BulkRequest.editSchema.getDefault();

		if (bulkRequestData && typeof bulkRequestData === 'object') {
			return { ...defaults, ...bulkRequestData };
		}
		return defaults;
	}, [bulkRequestData]);

	const formik = useFormik({
		initialValues,
		enableReinitialize: true,
		validationSchema: BulkRequest.editSchema,
		onSubmit: async (values) => {
			updateBulkRequest(BulkRequest.editSchema.cast(values, { stripUnknown: true }));
		},
	});

	const handleNavigate = useCallback(
		(e: MouseEvent<HTMLButtonElement>) => {
			navigate(e.currentTarget.getAttribute('data-route') as string);
		},
		[navigate],
	);

	const onCancelRequest = () => {
		confirmProps.openConfirm();
	};

	useEffect(() => {
		if (isCancelRequestSuccess) {
			navigate('/dashboard/bulk-requests');
		}
	}, [isCancelRequestSuccess, navigate]);

	if (!bulkRequestData || !paymentSummary) {
		return <TestSelectionSkeleton />;
	}

	const payment = new Payment(formik.values.tests);

	const amountDue = payment.total() - Number(paymentSummary.paidAmount);

	return (
		<>
			<Helmet>
				<title> Edit Bulk Request - {process.env.REACT_APP_ORG} </title>
			</Helmet>

			{isUpdateRequestSuccess && (
				<Toast body={'Request has been updated successfully!'} severity="success" autoHideDuration={3000} />
			)}

			{isUpdateRequestError && (
				<Toast body={'Failed to update your request!'} severity="error" autoHideDuration={3000} />
			)}

			{isCancelRequestError && (
				<Toast body={'Failed to cancel your request!'} severity="error" autoHideDuration={3000} />
			)}

			<form onSubmit={formik.handleSubmit}>
				<Stack spacing={2}>
					<Typography variant="h4" gutterBottom>
						Request {bulkRequestData.id}
					</Typography>

					<Card>
						<CardContent>
							<Box
								sx={{
									'& .RequestTestTotalKey, & .RequestTestTotalValue': {
										fontSize: theme.typography.h4.fontSize,
									},
								}}
							>
								<BulkRequestTest formik={formik} allowQtyEditing={allowQtyEditing} />
							</Box>
						</CardContent>
					</Card>

					<Card>
						<CardContent>
							<Stack spacing={2}>
								<Stack direction="row" justifyContent="space-between">
									<Typography variant="subtitle1" fontWeight="bold">
										Amount Paid
									</Typography>
									<Box sx={{ textAlign: 'right' }}>
										<Typography variant="subtitle1" sx={{ color: theme.palette.common.black }} fontWeight="bold">
											{fCurrency(paymentSummary.paidAmount)}
										</Typography>
									</Box>
								</Stack>
								<Stack direction="row" justifyContent="space-between">
									<Typography variant="h4" fontWeight="bold">
										Amount Due
									</Typography>
									<Box sx={{ textAlign: 'right' }}>
										<Typography variant="h4" sx={{ color: theme.palette.common.black }} fontWeight="bold">
											{fCurrency(Number(amountDue.toFixed(2)))}
										</Typography>
									</Box>
								</Stack>
							</Stack>
						</CardContent>
					</Card>

					<Card>
						<CardContent>
							<BulkRequestLocation formik={formik} />
						</CardContent>
						<CardActions sx={{ ml: 2 }}>
							<Stack direction="row" spacing={4} alignItems="center">
								<Button
									loading={isUpdateRequestLoading}
									variant="outlined"
									color="primary"
									type="submit"
									disabled={isUpdateRequestLoading}
								>
									<span>Update</span>
								</Button>

								<Button color="error" variant="outlined" data-route="/dashboard/bulk-requests" onClick={handleNavigate}>
									Back
								</Button>
							</Stack>
							<Grid container justifyContent="flex-end">
								<Grid>
									<Button
										loading={isCancelRequestLoading}
										variant="outlined"
										color="error"
										disabled={isUpdateRequestLoading || isCancelRequestLoading}
										onClick={onCancelRequest}
									>
										<span>Cancel Request</span>
									</Button>
								</Grid>
							</Grid>
						</CardActions>
					</Card>
				</Stack>
			</form>

			<Confirm
				{...confirmProps}
				title="Are you sure you want to cancel?"
				onConfirm={() => {
					confirmProps.closeConfirm();
					cancelBulkRequest(reqId);
				}}
			>
				<Typography variant="body1">Please note our cancellation policy:</Typography>

				<List dense>
					<ListItem sx={{ display: 'list-item', p: 0 }}>
						<ListItemText primary="Cancellations 7+ days prior to assessment receive a full refund." />
					</ListItem>

					<ListItem sx={{ display: 'list-item', p: 0 }}>
						<ListItemText primary="Cancellations made 2–7 days prior receive a 50% refund of the deposit." />
					</ListItem>

					<ListItem sx={{ display: 'list-item', p: 0 }}>
						<ListItemText primary="Cancellations within 48 hours are non-refundable." />
					</ListItem>
				</List>
			</Confirm>
		</>
	);
}
